import React, { useState } from 'react';
import { MealKey, DayKey, WeeklyMealPlan, MealPlanSubstitutions, DayMeals, FoodNutrients } from '../../types/mealPlan';
import { FoodAutocompleteInput } from './FoodAutocompleteInput';
import { getTrainingMealSchedule } from '../../utils/mealSchedule';
import './Plan.css';

const DAY_LABELS: Record<DayKey, string> = {
  monday: 'Segunda-feira',
  tuesday: 'Terça-feira',
  wednesday: 'Quarta-feira',
  thursday: 'Quinta-feira',
  friday: 'Sexta-feira',
  saturday: 'Sábado',
  sunday: 'Domingo',
};

const MEAL_LABELS: Record<MealKey, string> = {
  breakfast: 'Café da Manhã',
  morningSnack: 'Lanche da Manhã',
  lunch: 'Almoço',
  afternoonSnack: 'Lanche da Tarde',
  dinner: 'Jantar',
  eveningSnack: 'Ceia',
};

const MEAL_ORDER: MealKey[] = ['breakfast', 'morningSnack', 'lunch', 'afternoonSnack', 'dinner', 'eveningSnack'];

const emptyDayMeals: DayMeals = {
  breakfast: [],
  morningSnack: [],
  lunch: [],
  afternoonSnack: [],
  dinner: [],
  eveningSnack: [],
};

const emptyWeeklyPlan: WeeklyMealPlan = {
  monday: { ...emptyDayMeals },
  tuesday: { ...emptyDayMeals },
  wednesday: { ...emptyDayMeals },
  thursday: { ...emptyDayMeals },
  friday: { ...emptyDayMeals },
  saturday: { ...emptyDayMeals },
  sunday: { ...emptyDayMeals },
};

interface WeeklyMealPlanEditorProps {
  weeklyPlan: WeeklyMealPlan;
  onChange: (plan: WeeklyMealPlan) => void;
  substitutions?: MealPlanSubstitutions | null;
  onSubstitutionSelect?: (day: DayKey, mealType: MealKey, optionId: string) => void;
  onNutrientAdd?: (day: DayKey, meal: MealKey, index: number, nutrients: FoodNutrients, text: string) => void;
  onDayChange?: (day: DayKey) => void;
  trainingTime?: string;
}

export const WeeklyMealPlanEditor: React.FC<WeeklyMealPlanEditorProps> = ({
  weeklyPlan,
  onChange,
  substitutions,
  onSubstitutionSelect,
  onNutrientAdd,
  onDayChange,
  trainingTime,
}) => {
  const [selectedDay, setSelectedDay] = useState<DayKey>('monday');
  const trainingSchedule = trainingTime ? getTrainingMealSchedule(trainingTime) : null;

  const updateMealItem = (day: DayKey, mealKey: MealKey, index: number, text: string) => {
    const next = { ...weeklyPlan };
    const dayMeals = { ...next[day] };
    const mealItems = [...dayMeals[mealKey]];
    mealItems[index] = text;
    dayMeals[mealKey] = mealItems;
    next[day] = dayMeals;
    onChange(next);
  };

  const addMealItem = (day: DayKey, mealKey: MealKey) => {
    const next = { ...weeklyPlan };
    const dayMeals = { ...next[day] };
    dayMeals[mealKey] = [...dayMeals[mealKey], ''];
    next[day] = dayMeals;
    onChange(next);
  };

  const removeMealItem = (day: DayKey, mealKey: MealKey, index: number) => {
    const next = { ...weeklyPlan };
    const dayMeals = { ...next[day] };
    const mealItems = [...dayMeals[mealKey]];
    mealItems.splice(index, 1);
    dayMeals[mealKey] = mealItems.length > 0 ? mealItems : [];
    next[day] = dayMeals;
    onChange(next);
  };

  const currentDayMeals = weeklyPlan[selectedDay];

  return (
    <div className="ns-weekly-meal-plan-editor">
      <div className="ns-day-selector">
        {(Object.keys(DAY_LABELS) as DayKey[]).map((day) => (
          <button
            key={day}
            type="button"
            className={selectedDay === day ? 'active' : ''}
            onClick={() => { setSelectedDay(day); onDayChange?.(day); }}
          >
            {DAY_LABELS[day]}
          </button>
        ))}
      </div>

      <div className="ns-day-meals">
        <h3>{DAY_LABELS[selectedDay]}</h3>
        {MEAL_ORDER.map((mealKey) => (
          <div key={mealKey} className="ns-meal-section">
            <div className="ns-meal-header">
              <h4>{MEAL_LABELS[mealKey]}{trainingSchedule ? ` · ${trainingSchedule.times[mealKey]}` : ''}{trainingSchedule?.preWorkoutMeal === mealKey ? ' · Pré-treino' : ''}{trainingSchedule?.postWorkoutMeal === mealKey ? ' · Pós-treino' : ''}</h4>
              <button
                type="button"
                className="ns-btn-add"
                onClick={() => addMealItem(selectedDay, mealKey)}
              >
                + Adicionar item
              </button>
            </div>
            <div className="ns-meal-items">
              {currentDayMeals[mealKey].map((item, idx) => (
                <div key={idx} className="ns-meal-item">
                  <FoodAutocompleteInput
                    className="ns-input"
                    placeholder={`Ex: 1 xícara de aveia com banana`}
                    value={item}
                    onChange={(text) => updateMealItem(selectedDay, mealKey, idx, text)}
                    onNutrientChange={(nutrients, text) => onNutrientAdd?.(selectedDay, mealKey, idx, nutrients, text)}
                  />
                  <button
                    type="button"
                    className="ns-btn-remove"
                    onClick={() => removeMealItem(selectedDay, mealKey, idx)}
                  >
                    ×
                  </button>
                </div>
              ))}
              {currentDayMeals[mealKey].length === 0 && (
                <p className="ns-empty-meal">Nenhum item adicionado. Clique em "Adicionar item" para começar.</p>
              )}
            </div>

            {/* Mostrar substituições se disponíveis */}
            {substitutions && (() => {
              const mealSub = substitutions.mealSubstitutions?.find(
                s => s.day === selectedDay && s.mealType === mealKey
              );

              if (mealSub && mealSub.options.length > 1) {
                return (
                  <div className="ns-meal-substitutions">
                    <div className="ns-meal-substitutions-title">
                      🔄 Opções Alternativas:
                    </div>
                    <div className="ns-meal-substitution-options">
                      {mealSub.options.map((option, optIdx) => (
                        <button
                          key={option.id}
                          type="button"
                          className="ns-meal-substitution-option"
                          onClick={() => {
                            if (onSubstitutionSelect) {
                              onSubstitutionSelect(selectedDay, mealKey, option.id);
                            } else {
                              // Aplicar diretamente se não houver callback
                              const updatedPlan = { ...weeklyPlan };
                              const dayMeals = { ...updatedPlan[selectedDay] };
                              dayMeals[mealKey] = option.foods;
                              updatedPlan[selectedDay] = dayMeals;
                              onChange(updatedPlan);
                            }
                          }}
                        >
                          <span className="ns-meal-substitution-name">
                            Opção {optIdx + 1}
                          </span>
                          <span className="ns-meal-substitution-foods">
                            {option.foods.join(', ')}
                          </span>
                          <span className="ns-meal-substitution-macros">
                            {option.calories} kcal | P: {option.macros.protein}g | C: {option.macros.carbohydrates}g | G: {option.macros.fats}g
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        ))}
      </div>
    </div>
  );
};

// Re-exportar tipos do mealPlan.ts para compatibilidade
export { emptyWeeklyPlan, DAY_LABELS, MEAL_LABELS };

